/* BinaryAdmin.jsx — Master-admin control panel for the Binary MLM tree.
 * Spec:
 *   • Show global state (total nodes, level fills, slab status, project lock)
 *   • Master-only actions: Migrate · Trigger Pending Slabs · Tune Distribution · Project Close · Download Refund CSV
 *   • Tree-viewer is FOR ADMINS ONLY (front-end hides it from affiliates per spec)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GitFork, Lock, LockOpen, Download, Warning, Crown, ArrowsClockwise, ChartLineUp, Buildings, HandCoins, Coins } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function getAuthHeaders() {
  const tok = localStorage.getItem("flux_session_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function BinaryAdmin() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [state, setState] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [busy, setBusy] = useState("");
  const [lookupUserId, setLookupUserId] = useState("");
  const [tree, setTree] = useState(null);

  const refresh = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/binary/state`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/binary/revenue`, { headers: getAuthHeaders() }),
      ]);
      setState(s.data);
      setRevenue(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load binary state");
    }
  };

  useEffect(() => { refresh(); }, []);

  const doMigrate = async () => {
    if (!window.confirm("Run binary tree migration? This places every existing affiliate in the tree by join_date (idempotent).")) return;
    setBusy("migrate");
    try {
      const r = await axios.post(`${API}/admin/binary/migrate`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Migrated");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Migration failed"); }
    finally { setBusy(""); }
  };

  const fireSlabs = async () => {
    if (!window.confirm("Fire any slabs whose level threshold is reached but not yet triggered?")) return;
    setBusy("slabs");
    try {
      const r = await axios.post(`${API}/admin/binary/trigger-pending-slabs`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Done");
      (r.data?.fired || []).forEach(f => toast.message(`Slab ${f.level} fired (pool=${f.members_in_pool} members)`));
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Trigger failed"); }
    finally { setBusy(""); }
  };

  const toggleProjectClose = async () => {
    const willClose = !state?.project_closed;
    const word = willClose ? "CLOSE" : "RE-OPEN";
    if (!window.confirm(`${word} the project? This is the master kill-switch. ${willClose ? "All registrations & payouts will freeze." : "Network operations resume."}`)) return;
    setBusy("close");
    try {
      const r = await axios.post(`${API}/admin/binary/project-close`, {}, { headers: getAuthHeaders() });
      toast.success(`Project ${r.data?.project_closed ? "CLOSED" : "RE-OPENED"}`);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(""); }
  };

  const downloadRefundCsv = async () => {
    setBusy("csv");
    try {
      const r = await axios.get(`${API}/admin/binary/refund-csv`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `velox_refunds_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Refund CSV downloaded");
    } catch (err) { toast.error(err.response?.data?.detail || "CSV failed (requires project closed)"); }
    finally { setBusy(""); }
  };

  const lookupTree = async () => {
    if (!lookupUserId) return;
    setBusy("lookup");
    try {
      const r = await axios.get(`${API}/admin/binary/tree/${lookupUserId}?depth=5`, { headers: getAuthHeaders() });
      setTree(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
      setTree(null);
    } finally { setBusy(""); }
  };

  if (!state) {
    return <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-6"><p className="text-zinc-500 text-sm">Loading binary state…</p></div>;
  }

  return (
    <div className="space-y-4" data-testid="binary-admin">
      {/* ===== HEADER ===== */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <GitFork size={20} weight="duotone" className="text-violet-400" />
            <h2 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Binary MLM Tree</h2>
            {state.project_closed && (
              <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-400" data-testid="binary-closed-badge">PROJECT CLOSED</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="binary-refresh-btn" onClick={refresh}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md border border-zinc-700/50 text-zinc-300 hover:bg-zinc-800">
              <ArrowsClockwise size={11} /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <Stat label="Total Nodes" value={state.total_nodes} testid="stat-total-nodes" />
          <Stat label="Slab 4" value={state.slab4.triggered ? "Triggered" : `${state.level_counts?.["4"] || 0}/${state.slab4.required_at_level_4}`} tone={state.slab4.triggered ? "ok" : "warn"} testid="stat-slab-4" />
          <Stat label="Slab 8" value={state.slab8.triggered ? "Triggered" : `${state.level_counts?.["8"] || 0}/${state.slab8.required_at_level_8}`} tone={state.slab8.triggered ? "ok" : "warn"} testid="stat-slab-8" />
          <Stat label="Reserve Pool" value={formatINR(state.downline_support_reserve || 0)} tone="warn" testid="stat-reserve" />
        </div>

        {state.project_closed && (
          <p className="text-xs text-rose-300 mt-3" data-testid="project-closed-note">Project status: <span className="font-bold uppercase">{state.project_closed ? "CLOSED" : "Open"}</span></p>
        )}

        {/* Level fill breakdown */}
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Level Fills</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(state.level_counts || {}).sort((a, b) => +a[0] - +b[0]).map(([lv, count]) => (
              <div key={lv} className="text-[10px] font-mono px-2 py-1 rounded border border-zinc-800 bg-zinc-950/70 text-zinc-300">
                L{lv}: <span className="text-blue-400 font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== COMPANY REVENUE DASHBOARD ===== */}
      {revenue && (
        <div className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-zinc-900/80 to-emerald-950/20 p-5" data-testid="revenue-dashboard">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={18} weight="duotone" className="text-emerald-400" />
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Company Revenue Dashboard</h3>
            <span className="ml-auto text-[10px] text-emerald-400/70 font-mono">{revenue.summary.total_signups} signups</span>
          </div>

          {/* Revenue cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <RevenueCard
              testid="rev-total-collected"
              label="Total Collected"
              value={formatINR(revenue.summary.total_collected_inr)}
              icon={HandCoins}
              color="#3b82f6"
              sub={`${revenue.summary.total_signups} × ₹2,999`}
            />
            <RevenueCard
              testid="rev-admin-fees"
              label="Admin Fees (₹600/signup)"
              value={formatINR(revenue.summary.company_admin_fees_inr)}
              icon={Coins}
              color="#10B981"
              sub="20% of every signup"
            />
            <RevenueCard
              testid="rev-slab-extras"
              label="Slab Company Extras"
              value={formatINR(revenue.summary.company_slab_extras_inr)}
              icon={Crown}
              color="#A855F7"
              sub="20% of slab pool"
            />
            <RevenueCard
              testid="rev-total"
              label="Company Total Revenue"
              value={formatINR(revenue.summary.company_total_revenue_inr)}
              icon={ChartLineUp}
              color="#FBBF24"
              sub="Fees + slab extras"
              highlight
            />
          </div>

          {/* Liability cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <LiabilityCard testid="liab-locked" label="Total Locked" value={formatINR(revenue.liability.total_locked_balance_inr)} tone="warn" />
            <LiabilityCard testid="liab-withdrawable" label="Total Withdrawable" value={formatINR(revenue.liability.total_withdrawable_balance_inr)} tone="ok" />
            <LiabilityCard testid="liab-unlocked-users" label="Unlocked Users" value={`${revenue.liability.unlocked_users} / ${revenue.liability.unlocked_users + revenue.liability.locked_users}`} tone="info" />
            <LiabilityCard testid="liab-refund" label="Refund Liability" value={formatINR(revenue.liability.potential_refund_liability_if_closed_inr)} tone="danger" />
          </div>

          {/* Daily signups sparkline */}
          {revenue.daily_signups_30d.length > 0 && (
            <div className="rounded-lg border border-zinc-800/50 bg-zinc-950/40 p-3" data-testid="signups-chart">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Daily Signups (last 30 days)</p>
              <SignupSparkline data={revenue.daily_signups_30d} />
            </div>
          )}

          {/* Slab events log */}
          {revenue.slab_events.length > 0 && (
            <div className="mt-3 rounded-lg border border-zinc-800/50 bg-zinc-950/40 p-3" data-testid="slab-events-log">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Slab Events Fired</p>
              <div className="space-y-2">
                {revenue.slab_events.map((e) => (
                  <div key={e.id} className="text-xs flex items-center justify-between" data-testid={`slab-event-${e.level}`}>
                    <div>
                      <span className="font-bold text-purple-400">Slab {e.level}</span>
                      <span className="text-zinc-500 ml-2">pool: {formatINR(e.pool)}</span>
                      <span className="text-zinc-500 ml-2">+ reserve: {formatINR(e.reserve_boost)}</span>
                    </div>
                    <span className="text-emerald-400 font-mono">Co: +{formatINR(e.company_extra)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== ADMIN ACTIONS ===== */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-5">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Master Actions</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <ActionButton testid="binary-migrate-btn" onClick={doMigrate} busy={busy === "migrate"} icon={ArrowsClockwise} label="Migrate Existing" disabled={!isMaster} />
          <ActionButton testid="binary-fire-slabs-btn" onClick={fireSlabs} busy={busy === "slabs"} icon={Crown} label="Fire Pending Slabs" disabled={!isMaster} />
          <ActionButton testid="binary-project-close-btn" onClick={toggleProjectClose} busy={busy === "close"} icon={state.project_closed ? LockOpen : Lock} label={state.project_closed ? "Re-open Project" : "Close Project"} disabled={!isMaster} tone={state.project_closed ? "ok" : "danger"} />
          <ActionButton testid="binary-refund-csv-btn" onClick={downloadRefundCsv} busy={busy === "csv"} icon={Download} label="Refund CSV" disabled={!isMaster || !state.project_closed} />
        </div>
      </div>

      {/* ===== DISTRIBUTION CONFIG (read-only display) ===== */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-5" data-testid="binary-dist-config">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Distribution Config (₹2,999 entry → ₹2,399 pool after ₹600 admin)</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
          <ConfigStat label="Upline Cascade %" value={`${(state.distribution_config.upline_cascade_pct * 100).toFixed(0)}% per level`} />
          <ConfigStat label="Slab Wallet %" value={`${(state.distribution_config.slab_wallet_pct * 100).toFixed(0)}%`} />
          <ConfigStat label="Slab Downline %" value={`${(state.distribution_config.slab_downline_pct * 100).toFixed(0)}%`} />
          <ConfigStat label="Slab Upline %" value={`${(state.distribution_config.slab_upline_pct * 100).toFixed(0)}%`} />
          <ConfigStat label="Slab Co Extra %" value={`${(state.distribution_config.slab_company_extra_pct * 100).toFixed(0)}%`} />
        </div>
        <p className="text-[10px] text-zinc-500 mt-3 italic">
          Standard cycle: <span className="text-zinc-300">100% of ₹2,399 pool cascades upward (20% per upline, unlimited depth)</span>. Residual flows into the Reserve and is added to the next slab&apos;s downline pool.
        </p>
      </div>

      {/* ===== TREE LOOKUP (ADMIN-ONLY, NEVER SHOWN TO AFFILIATES) ===== */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-5" data-testid="binary-tree-lookup">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Tree Lookup <span className="text-rose-400">(Admin Only)</span></p>
        <p className="text-[11px] text-zinc-500 mb-3">
          Enter any of: referral code (<span className="font-mono text-zinc-400">FLUX...</span>), display ID (<span className="font-mono text-zinc-400">VAT-000021</span>), email, or internal user_id (<span className="font-mono text-zinc-400">aff_xxx</span>).
        </p>
        <div className="flex gap-2 mb-3">
          <input data-testid="binary-tree-lookup-input" type="text" value={lookupUserId} onChange={(e) => setLookupUserId(e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === "Enter" && lookupUserId && busy !== "lookup") lookupTree(); }}
            placeholder="FLUXABC123 · VAT-000021 · alice@x.com · aff_xxx"
            className="flex-1 px-3 py-1.5 text-xs rounded-md bg-zinc-950 border border-zinc-800 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 font-mono" />
          <button data-testid="binary-tree-lookup-btn" onClick={lookupTree} disabled={!lookupUserId || busy === "lookup"}
            className="px-3 py-1.5 text-[11px] font-semibold rounded-md border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 disabled:opacity-40">
            {busy === "lookup" ? "Looking…" : "Inspect Subtree"}
          </button>
        </div>
        {tree && <SubtreeView node={tree} />}
      </div>

      {/* ===== WARNING ===== */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex gap-2 items-start" data-testid="binary-admin-warning">
        <Warning size={16} weight="duotone" className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          Affiliates do <strong>NOT</strong> see the tree on their dashboard (spec requirement).
          They only see wallet (Withdrawable + Locked), direct counts (L/R) and team size.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneClass = tone === "ok" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : tone === "danger" ? "text-rose-400" : "text-blue-400";
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
      <p className={`text-lg font-bold ${toneClass}`} style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-[10px] text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}

function ConfigStat({ label, value }) {
  return (
    <div className="rounded border border-zinc-800/60 bg-zinc-950/30 px-2 py-1.5">
      <p className="text-zinc-200 font-mono font-bold">{value}</p>
      <p className="text-zinc-500 text-[9px]">{label}</p>
    </div>
  );
}

function ActionButton({ testid, onClick, busy, icon: Icon, label, disabled, tone }) {
  const toneClass = tone === "danger"
    ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
    : tone === "ok"
      ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
      : "border-blue-500/40 text-blue-300 hover:bg-blue-500/10";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-md border bg-transparent transition disabled:opacity-30 disabled:cursor-not-allowed ${toneClass}`}>
      <Icon size={12} className={busy ? "animate-spin" : ""} />
      {busy ? "Working…" : label}
    </button>
  );
}

function RevenueCard({ testid, label, value, icon: Icon, color, sub, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-3 transition-all ${
        highlight
          ? "border-emerald-500/50 bg-emerald-500/10"
          : "border-zinc-800 bg-zinc-950/40"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} weight="duotone" style={{ color }} />
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      </div>
      <p className="text-lg font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function LiabilityCard({ testid, label, value, tone }) {
  const toneClass = tone === "warn" ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
    : tone === "ok" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
    : tone === "danger" ? "text-rose-300 border-rose-500/30 bg-rose-500/5"
    : "text-blue-300 border-blue-500/30 bg-blue-500/5";
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 ${toneClass}`}>
      <p className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{label}</p>
      <p className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>{value}</p>
    </div>
  );
}

function SignupSparkline({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, idx) => {
        const h = Math.max(2, (d.count / max) * 60);
        return (
          <div
            key={idx}
            className="flex-1 bg-emerald-400/60 hover:bg-emerald-400 rounded-t transition-all"
            style={{ height: `${h}px` }}
            title={`${d.date}: ${d.count} signups`}
            data-testid={`sparkline-day-${d.date}`}
          />
        );
      })}
    </div>
  );
}

// ==================== SUBTREE VIEW ====================
// Renders the queried subtree as an actual VISUAL binary tree — root
// centered at the top, left/right children fanning below with connector
// lines. Each node is a card with the user's identity + leg + wallet
// summary. Empty slots are drawn as dashed "empty" placeholders so admins
// can see holes in the placement at a glance.
function SubtreeView({ node }) {
  if (!node) return null;
  if (node.missing) return <div className="text-xs text-rose-400">User not found in tree</div>;
  return (
    <div
      data-testid="binary-subtree-rows"
      className="overflow-x-auto pb-2"
    >
      <div className="inline-block min-w-full pt-2">
        <TreeNode node={node} isRoot />
      </div>
      <div className="mt-3 flex items-center gap-4 text-[10px] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-blue-500/60 border border-blue-500/70" /> L (left leg)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500/60 border border-emerald-500/70" /> R (right leg)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm border border-dashed border-zinc-600" /> empty slot
        </span>
      </div>
    </div>
  );
}

function TreeNode({ node, isRoot = false }) {
  if (!node) return <EmptySlot />;
  if (node.missing) return <EmptySlot label="missing" />;

  const hasChildren = node.left || node.right;

  return (
    <div className="flex flex-col items-center">
      <NodeCard node={node} isRoot={isRoot} />

      {hasChildren && (
        <>
          {/* Vertical connector from card down to the horizontal bus */}
          <div className="w-px h-4 bg-zinc-700" />

          {/* Horizontal bus with two branches */}
          <div className="relative flex items-start justify-center gap-6 md:gap-10 pt-4">
            {/* horizontal bar spanning left → right */}
            <div className="absolute top-0 left-1/4 right-1/4 h-px bg-zinc-700" />
            {/* vertical stubs down to each child */}
            <div className="absolute top-0 left-1/4 w-px h-4 bg-zinc-700" />
            <div className="absolute top-0 right-1/4 w-px h-4 bg-zinc-700" />

            <div className="flex flex-col items-center">
              {React.createElement(TreeNode, { node: node.left })}
            </div>
            <div className="flex flex-col items-center">
              {React.createElement(TreeNode, { node: node.right })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NodeCard({ node, isRoot }) {
  // Backend `binary_placements.leg` is stored as 'left'/'right' (full word).
  // Older code paths use 'L'/'R'. Normalise to a single uppercase letter so
  // the visual leg tint / badge work regardless of the source format.
  const leg = (node.leg || "").toString().charAt(0).toUpperCase();

  const legStyle =
    leg === "L"
      ? { border: "1px solid rgba(59,130,246,0.5)", background: "rgba(59,130,246,0.08)" }
      : leg === "R"
      ? { border: "1px solid rgba(16,185,129,0.5)", background: "rgba(16,185,129,0.08)" }
      : { border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.02)" };

  return (
    <div
      className="min-w-[180px] max-w-[220px] rounded-lg px-3 py-2 text-xs shadow-sm"
      style={{
        ...legStyle,
        ...(isRoot ? { boxShadow: "0 0 0 2px rgba(0,180,216,0.35)" } : {}),
      }}
      data-testid={`tree-node-${node.user_id || "root"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        {/* Root badge wins over leg badge — the queried head node always
            shows "Root" even if it has a placement leg value. */}
        {isRoot ? (
          <span
            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: "rgba(0,180,216,0.2)", color: "#00b4d8" }}
            data-testid="tree-node-badge-root"
          >
            Root
          </span>
        ) : leg === "L" ? (
          <span
            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: "rgba(59,130,246,0.2)", color: "#93c5fd" }}
            data-testid="tree-node-badge-leg-L"
          >
            L-leg
          </span>
        ) : leg === "R" ? (
          <span
            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: "rgba(16,185,129,0.2)", color: "#6ee7b7" }}
            data-testid="tree-node-badge-leg-R"
          >
            R-leg
          </span>
        ) : (
          <span />
        )}
        {node.depth != null && (
          <span className="text-[9px] font-mono text-zinc-500">L{node.depth}</span>
        )}
      </div>

      <div className="text-[12px] font-bold text-white truncate" title={node.name || node.email}>
        {node.name || node.email || node.user_id}
      </div>

      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
        {node.referral_code && (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300">
            {node.referral_code}
          </span>
        )}
        {node.display_id && (
          <span className="text-[9px] font-mono text-zinc-500">{node.display_id}</span>
        )}
      </div>

      {node.wallet && (
        <div className="text-[9px] text-zinc-500 font-mono mt-1 flex items-center justify-between">
          <span title="Locked balance">
            L: <span className="text-amber-400">₹{(node.wallet.locked_balance || 0).toFixed(0)}</span>
          </span>
          <span title="Withdrawable">
            W: <span className="text-emerald-400">₹{(node.wallet.withdrawable_balance || 0).toFixed(0)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function EmptySlot({ label = "empty" }) {
  return (
    <div
      className="min-w-[140px] max-w-[180px] rounded-lg px-3 py-3 text-center border border-dashed border-zinc-700 bg-zinc-950/40"
      data-testid="tree-node-empty"
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-600">{label}</div>
    </div>
  );
}
